import { TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';

/** Diameter of the avatar circle. The node is placed by this so the circle — not the whole row — lands
 *  centred on the spine; see {@link SequenceOwnerNode}'s call site in SequenceGraphEditor. */
export const OWNER_AVATAR_PX = 40;

interface Props {
  ownerName: string | null;
  ownerProfilePicture: string | null;
  /** Render the interactive affordances (hover ring + accent on the name). */
  editable: boolean;
  /** The picker is open — hold the trigger in its active state. */
  active: boolean;
}

/**
 * The owner's face + name, as drawn at the top of the sequence spine. Pure presentation: the circle sits
 * first so the node can be positioned by its left edge, with the name flowing to its right.
 */
export function SequenceOwnerBadge({ ownerName, ownerProfilePicture, editable, active }: Props) {
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-white ring-2 ring-lynch-200 transition-colors dark:bg-slate-900 dark:ring-slate-600',
          editable && 'group-hover/owner:ring-blue-400 dark:group-hover/owner:ring-blue-400',
          active && 'ring-blue-400 dark:ring-blue-400',
        )}
        style={{ width: OWNER_AVATAR_PX, height: OWNER_AVATAR_PX }}>
        <TeamUserAvatar
          userTeam={{ name: ownerName, profilePicture: ownerProfilePicture }}
          shape="circle"
          className="size-9 text-xs"
        />
      </span>
      <span className="flex flex-col whitespace-nowrap text-left">
        <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Runs as
        </span>
        <span
          className={cn(
            'text-xs font-medium text-lynch-600 dark:text-lynch-300',
            editable && 'group-hover/owner:text-blue-500 dark:group-hover/owner:text-blue-400',
          )}>
          {ownerName ?? 'No owner'}
        </span>
      </span>
    </span>
  );
}
